(ns quuppa.qmd.code-reader
  "Code reader component which uses html5-qrcode library."
  (:require [clojure.string :as str]
            [goog.object :as gobj]
            [re-frame.core :as rf]
            [potpuri.core :as p]
            ["html5-qrcode" :refer [Html5Qrcode]]
            ["react" :as react]))


(def code-formats
  "Which codes should the reader be able to read.
  Basically what ZXing has + possible impls from html5-qrcode:
  https://scanapp.org/html5-qrcode-docs/docs/apis/enums/Html5QrcodeSupportedFormats"
  ;; HACK nr 6 is dirty hack to enable 'data_matrix' selection on BarcodeDetector side in HTML5Qrcode.
  ;; Problem is that BarcodeDetectorDelegate.formatMap has formats as TS enum and the values are lower cased.
  ;; But the primary Html5QrcodeSupportedFormatsTextMap has them UPPER CASED.
  ;; There is validation that will discard "data_matrix" if passed, and result is that there is error initializing
  ;; the new BarcodeDetector(formats) [native-bar-code-detector.ts:122], because 'data_matrix' was earlier filtered
  ;; out because it is not in the 'main' enum. And can't initialize BarcodeDetector with empty formats.
  ;; Because TS enums are actually numbered (there is no Enum type in JS) mappings and both enums (Html5QrcodeSupportedFormatsTextMap
  ;; and BarcodeDetectorDelegate.formatMap) happen to have "data_matrix/DATA_MATRIX" as their seventh element. thus
  ;; nr 6 passes through the first validation layer and then in BarcodeDetector side it matches the "data_matrix" value.
  ;; LOL :D Bug reported to GitHub: https://github.com/mebjas/html5-qrcode/issues/824
  #js ["DATA_MATRIX" 6])

;; qrbox function: https://scanapp.org/html5-qrcode-docs/docs/apis/interfaces/Html5QrcodeCameraScanConfig#qrbox
(defn qr-scanner-box-dimensions
  "Calculate square 'focus box' into the middle of the scanning UI."
  [view-width view-height]
  ;; Use 40% of bigger dimension, but no larger than other one.
  ;; Idea from original impl by Juho Teperi.
  (let [scaling (-> (* 0.5 (max view-height view-width))
                    (min view-height)
                    (min view-width))
        aspect (/ view-width view-height)]
    #js {:width  (* scaling aspect)
         :height (* scaling aspect)}))


(defn start [html5-qrcode-reader configuration {:keys [on-result on-error on-init on-device-id]}]
  (js/console.log "Starting code reader, with config:" (clj->js configuration))
  (let [on-error* (fn on-error* [error]
                    (cond
                      (str/includes? error "NotFoundException") nil
                      (str/includes? error "No barcode") nil
                      :else (js/console.warn "scan error" error)))]

    (-> (.start html5-qrcode-reader
                (or (get-in configuration [:videoConstraints :deviceId :exact]) #js {})
                ;; see Html5QrcodeCameraScanConfig: https://scanapp.org/html5-qrcode-docs/docs/apis/interfaces/Html5QrcodeCameraScanConfig
                (clj->js configuration #_(update configuration :videoConstraints (partial p/remove-vals nil?)))
                on-result
                on-error*)
        (.then (fn []
                 ;; .getCapabilities is not availabe on Firefox
                 (let [capabilities (try
                                      (.getRunningTrackCapabilities html5-qrcode-reader)
                                      (catch :default e
                                        (js/console.warn "Stream getCapabilities not supported" e)))
                       settings (.getRunningTrackSettings html5-qrcode-reader)]
                   (js/console.debug "Stream video settings" settings)
                   (when capabilities
                     (js/console.debug "Stream capabilities" capabilities))
                   (when on-device-id
                     (on-device-id (.-deviceId settings))))
                 (on-init html5-qrcode-reader)))
        (.catch (fn [error]
                  (js/console.warn "Camera start error" error)
                  ;; HTML5Qrcode does not expose the error object, instead it just returns strings...
                  ;; Possible improvement could be submitted upstream. Below was old code with zxing error object.
                  #_(if (= "NotAllowedError" (.-name error))
                    (on-error {:reason :no-camera-permission
                               :err-msg (.-message error)})
                    (on-error {:reason :camera-error
                               :err-msg (str (.-name error) ": " (.-message error))}))
                  ;; Seems some of HTML5Qrcode error messages are split with ' = ${error}' where
                  ;; the latter part is error msg from depths. Display that one only, or else the whole string
                  ;; if there was no match for split.
                  (let [[beginning tail-msg] (str/split error #"=\s+" 2)]
                    (if (and (not-empty tail-msg) (str/starts-with? tail-msg "NotReadableError"))
                      (on-error {:reason  :camera-error
                                 :err-msg "Camera not readable. Could another process be using the camera?"})
                      (on-error {:reason  :camera-error
                                 :err-msg (or (not-empty tail-msg) beginning)}))))))))

(def device-id-key "QMD_CAMERA_DEVICE_ID")

(defn scanning? [html5-qrcode-obj]
  (if html5-qrcode-obj
    ;; enum Html5QrcodeScannerState -> SCANNING = 2
    (= 2 (.getState html5-qrcode-obj))
    false))

(defn code-reader [opts]
  (let [reader (react/useRef nil)
        stop-p (react/useRef nil)
        [devices set-devices] (react/useState nil)
        [active-camera-id set-active-camera-id] (react/useState (js/localStorage.getItem device-id-key))
        active-camera-idx (or (->> (map vector devices (range))
                                   (some (fn [[device idx]]
                                           (when (= (:deviceId device) active-camera-id)
                                             idx))))
                              0)

        [device-capabilities set-capabilities!] (react/useState nil)
        [video-settings set-settings!] (react/useState nil)
        [video-constraints set-constraints!] (react/useState {:deviceId    (when active-camera-id
                                                                             {:exact active-camera-id})
                                                              :facingMode  "environment"
                                                              ;; Width = height, height = width in portrait mode.
                                                              ;; These are according to the camera sensor orientation.
                                                              ;:height      {:ideal 1440}
                                                              ;:width       {:ideal 1920}
                                                              ;; TODO: using 4:3 ratio, I guess because it would give the most
                                                              ;;   best image quality from mobile phones. But it makes the camera
                                                              ;;   view not to be 100% height in the page. It should be possible to
                                                              ;;   scale from/to 4:3 -> 16:9, but that is left for later exercise currently.
                                                              :aspectRatio {:ideal 1.33}
                                                              :frameRate   {:ideal 30}
                                                              :sharpness   7
                                                              :focusMode   {:ideal "continuous"}
                                                              :zoom        1})

        [error set-error] (react/useState nil)
        [unmounting? set-unmounting!] (react/useState false)

        apply-constraints (fn [constraints]
                            (if-let [rdr (.-current reader)]
                              (if (scanning? rdr)
                                (-> (.applyVideoConstraints rdr
                                                            #js {:advanced #js [(clj->js constraints)]})
                                    (.catch (fn [e]
                                              (js/console.error "Can't apply constraint" (pr-str constraints) e))))
                                (js/console.warn "Camera not initialized for scanning"))
                              (js/console.warn "No reader, can't apply constraints")))


        opts (assoc opts
                    ;; TODO: Clear up start fn opts,
                    ;; so there is no need to wrap the functions here.
                    :on-init (fn [reader]
                               (set-error nil)
                               (set-settings! (js->clj (.getRunningTrackSettings reader) :keywordize-keys true))
                               (when-let [caps (try
                                                 ;; capabilities not available on Firefox/Edge https://wpt.fyi/results/mediacapture-streams/MediaStreamTrack-getCapabilities.https.html?label=master&label=experimental&aligned
                                                 (.getRunningTrackCapabilities reader)
                                                 (catch :default e
                                                   (js/console.warn "Stream getCapabilities not supported" e)))]
                                 (set-capabilities! (js->clj caps :keywordize-keys true)))
                               (when-let [f (:on-init opts)]
                                 (f reader)))
                    :on-error (fn [error]
                                (case (:reason error)
                                  :camera-error (set-error error)
                                  ((:on-error opts) error)))
                    :on-device-id (fn [id] (set-active-camera-id id)))]

    (react/useEffect
      (fn []
        (set! (.-current reader) (Html5Qrcode. "code-reader-video" #js {:formatsToSupport code-formats}))
        (fn []
          (set-unmounting! true)))
      ;; on initial render, and when unmounting the component
      #js [])

    (react/useEffect (fn []
                       (when (not-empty active-camera-id)   ; avoids nil to be written as 'null' into LocalStorage...
                         (let [old (js/localStorage.getItem device-id-key)]
                           (js/localStorage.setItem device-id-key active-camera-id)
                           ;; Check if old was empty, then we don't want to toggle camera as it would stop
                           ;; the camera on initial render and causing some glitches, so we are fine with null
                           ;; deviceId in the state (because you can ask for camera without deviceId and it will work fine).
                           ;; If user then uses "Toggle camera" to change it then we would have non-empty 'old' value and
                           ;; this would cause the stop/start of the video stream.
                           (when (not-empty old)
                             ;; updating constraints to new camera will force stop + start of the scanner camera
                             ;; as camera can't be 'dynamically' changed using applyConstraints.
                             (set-constraints! (fn [state] (assoc-in state [:deviceId :exact] active-camera-id)))))))
                     #js [active-camera-id])

    (react/useEffect (fn []
                       (if js/navigator.mediaDevices
                         ;; getCameras from HTML5Qrcode is bad, it tries to open stream (and close them immediately)
                         ;; and only then tries to enumerateDevices, not sure why but doesn't make sense.
                         ;; And does not work in our case, because we start streaming immediately on render, so
                         ;; what happens is that the underneath promise call to getCameras gets rejected with error,
                         ;; because it can't open the stream on one of the devices, because we just opened the feed from it.
                         ;; so doing enumerateDevices very naturally here...
                         (-> #_(.getCameras Html5Qrcode)
                             (js/navigator.mediaDevices.enumerateDevices)
                             (.then (fn [devices]
                                      (let [sorted (->> devices
                                                        ;; devices are of type InputDeviceInfo (or super MediaDeviceInfo)
                                                        ;; js->clj doesn't have conversion defined on them, thus we manually
                                                        ;; here construct the object
                                                        (map (fn [^js/MediaDeviceInfo d]
                                                               {:label (gobj/get d "label")
                                                                :kind (gobj/get d "kind")
                                                                :deviceId (gobj/get d "deviceId")
                                                                :groupId (gobj/get d "groupId")}))
                                                        (filter #(= "videoinput" (:kind %)))
                                                        (sort-by :label)
                                                        (vec))]
                                        (set-devices sorted)
                                        ;; TODO could pick "best" camera by inspecting their stream capabilities
                                        (js/console.debug "Devices:" (clj->js sorted))
                                        sorted)))
                             (.catch (fn [err]
                                       (js/console.error "Error getting cameras: " err))))
                         (js/console.error "No navigator.mediaDevices API")))
                     #js [])

    (react/useEffect
      (fn []
        (let [rdr          (.-current reader)
              scan-on?     (scanning? rdr)
              video-config {:videoConstraints video-constraints
                            :verbose          true
                            :fps              10
                            :qrbox            qr-scanner-box-dimensions}]
          (js/console.debug "Starting up reader, scanning ? " scan-on?)
          (if (not scan-on?)
            (start rdr video-config opts)
            ;; scanner still on, probably initiated async stop earlier,
            ;; wait for the stop promise to be fulfilled before starting code reader again
            ;; this happens when video-constraints change, such as when switching to another camera
            (-> (.-current stop-p)
                (.then (fn []
                         (set! (.-current stop-p) nil)
                         (when-not unmounting?
                           (start rdr video-config opts))))))
          (fn []
            (js/console.debug "closing effect, cleaning up reader:" rdr)
            (let [stop* (.stop rdr)]
              (set! (.-current stop-p) stop*)
              ;; store the promise, so we can use (.then) and re-start the camera as soon as stop promise is ready
              (-> stop*
                  (.then (fn []
                           (js/console.debug "Reader stopped")))
                  (.catch (fn [e] (js/console.error "Error on unmount, stopping the reader caused:" e))))))))
      #js [video-constraints unmounting?])

    [:div.code-reader-container
     ;; TODO: Portrait video
     ;; from html5-qrcode:
     ;; "Ideally do not set the height of this container as the height should depend on the height of the video feed
     ;; from the camera. The height is derived from the aspect ratio of the video feed."
     ;; HTML5Qrcode will render the UI to below div
     [:div#code-reader-video]
     (when error
       [:div.code-reader-error
        [:h1.text-white (:err-msg error)]])

     [:div.code-reader-controls
      (when-let [zoom (:zoom device-capabilities)]
        [:div.zoom-input.input-group
         [:input#camera-zoom.form-range
          {:type          "range"
           :title         "Camera zoom"
           :min           (:min zoom)
           :max           (:max zoom)
           :step          (or (:step zoom) 1)
           :default-value (:zoom video-settings)
           :on-change     (fn [e]
                            (let [zoom-val (-> e .-target .-value)]
                              (apply-constraints {:zoom zoom-val})))}]
         [:label.form-label.visually-hidden {:for "camera-zoom"} "Zoom"]])]


     (when (>= (count devices) 2)
       [:div.code-reader-cameras
        [:button.btn.btn-primary
         {:on-click (fn [_e]
                      (let [current-idx (p/find-index devices {:deviceId active-camera-id})
                            next-idx (mod (inc current-idx) (count devices))
                            next-id (:deviceId (get devices next-idx))]
                        (set-active-camera-id next-id)))}
         "Toggle camera (" (inc active-camera-idx) "/" (count devices) ")"]])
     (when js/goog.DEBUG
       [:div.code-reader-debug
        [:button.btn.btn-primary
         {:on-click (fn [_]
                      (rf/dispatch [:modal/open-modal
                                    {:title   "Current video settings"
                                     :content (-> (.getRunningTrackSettings (.-current reader))
                                                  (js->clj :keywordize-keys true)
                                                  (pr-str))}]))}
         "Current settings"]
        [:button.btn.btn-primary
         {:on-click (fn [_]
                      (rf/dispatch [:modal/open-modal
                                    {:title   "streamVideoCapabilitiesGet"
                                     :content (pr-str device-capabilities)}]))}
         "streamVideoCapabilitiesGet"]])]))

(comment
  ;; streamVideoCapabilitiesGet output from my Asus Zenfone 8 Android camera
  {:colorTemperature     {:max 7000, :min 2850, :step 50},
   :groupId              "764ae1e34c5940ff63660bda1c081288bfb39996280c9e14e8525e511b94c13b",
   :zoom                 {:max 4, :min 1, :step 0.1},
   :facingMode           ["environment"],
   :deviceId             "2cb8f0726023d426f4c3746714e0ed32b34edf6338746a8ab56273b36789b803",
   :iso                  {:max 3200, :min 25, :step 1},
   :aspectRatio          {:max 4032, :min 3.3068783068783067E-4},
   :frameRate            {:max 30, :min 0},
   :width                {:max 4032, :min 1},
   :exposureCompensation {:max 2, :min -2, :step 0.1666666716337204},
   :exposureMode         ["continuous" "manual"],
   :focusDistance        {:max 1.2276785373687744, :min 0.03999999910593033, :step 0.009999999776482582},
   :focusMode            ["manual" "single-shot" "continuous"],
   :whiteBalanceMode     ["continuous" "manual"],
   :exposureTime         {:max 10000, :min 0, :step 0},
   :resizeMode           ["none" "crop-and-scale"],
   :torch                true,
   :height               {:max 3024, :min 1}}
  )
